import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_URL } from '@shared/config';
import { Filters, Layout, LayoutFilter } from '@shared/models';
import { IsValidLayout, SubjectAreaLayout } from '@shared/models/layout.model';
import { GlobalFilterService } from '@shared/services/global-filter.service';
import { KpiBoxService } from '@shared/services/kpi-box.service';
import { KpiService } from '@shared/services/kpi.service';
import { StringCompare } from '@shared/value-objects/string-compare';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { first, map, take } from 'rxjs/operators';
import { HttpErrorSkip } from '../../interceptors/http-error/http-error-skip.model';
import { APP_CONFIG, ConfigSettings } from '../config/config';
import { DashboardService } from './dashboard.service';
import { DashboardMetadata } from '@observation/shared/components/chatbot/agents/metadata-discovery/metadata-discovery.model';

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  public activeLayout: Layout;
  private layouts = new ReplaySubject<SubjectAreaLayout[]>(1);
  public layouts$ = this.layouts.asObservable();

  private layoutSelectedId = new ReplaySubject<number>(1);
  public layoutSelectedId$ = this.layoutSelectedId.asObservable();

  private globalSettings: ConfigSettings;

  private refreshPowerBi = new Subject<void>();
  public refreshPowerBi$ = this.refreshPowerBi.asObservable();

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) settings: ConfigSettings,
    private kpiBoxService: KpiBoxService,
    private globalFilterService: GlobalFilterService,
    private dashboardService: DashboardService,
    private kpiService: KpiService
  ) {
    this.globalSettings = settings;
  }

  allowedFilters(): Observable<Filters> {
    const filtersCached$ = this.globalFilterService.globalFiltersCached$;

    return filtersCached$.pipe(
      take(1),
      map(filtersCached => {
        let defaultValuesCached: Filters = {};
        let newGlobalFilters: Filters = {};
        defaultValuesCached = filtersCached
          .filter(
            filterCached =>
              filterCached.defaultValues &&
              (filterCached.isSubjectAreaFilter ||
                this.activeLayout.layoutFilters
                  .map(layoutFilter => layoutFilter.globalFilterId)
                  .includes(filterCached.globalFilterId))
          )
          .map(filterCached => filterCached.defaultValues)
          .reduce(
            (groupedDefaultValues: Filters, defaultValues: Filters = {}) => ({
              ...groupedDefaultValues,
              ...defaultValues,
            }),
            {}
          );
        newGlobalFilters = defaultValuesCached;
        return newGlobalFilters;
      })
    );
  }

  getSelectedLayoutFilters(): LayoutFilter[] {
    return this.activeLayout?.layoutFilters;
  }

  public changeLayouts(layouts: SubjectAreaLayout[]): void {
    this.layouts.next(layouts);
  }

  setLayout(layout: Layout) {
    const defaultValues$ = this.allowedFilters();
    if (layout.kpis?.length) {
      defaultValues$.subscribe((defaultValues: Filters) => {
        this.activeLayout = layout;
        this.globalFilterService.setGlobalFiltersSelected(defaultValues);
        this.kpiBoxService.changeKpis(layout.kpis);
      });
    } else {
      defaultValues$.subscribe((defaultValues: Filters) => {
        this.activeLayout = layout;
        this.globalFilterService.setGlobalFiltersSelected(defaultValues);
        this.dashboardService.selectDashboard(this.activeLayout.dashboard);
        this.kpiService.setActiveKpiContext({
          kpi: null,
          dashboard: this.activeLayout.dashboard,
        });
      });
    }
  }

  changeLayout(layout: Layout): void {
    // Avoid calling contenTiles twice when navigating betweeen layouts
    // having the component already initiated
    this.dashboardService.selectDashboard(undefined);
    this.activeLayout = layout;
    this.setLayout(layout);
  }

  searchLayout(searchNames: string[]): Observable<SubjectAreaLayout> {
    return this.layouts$.pipe(
      first(),
      map(layouts => {
        const layoutNames = layouts.map(layout => layout.layoutName);
        const stringCompare = new StringCompare(searchNames, layoutNames);
        const search = stringCompare.compareArrays();

        const layoutMatch = layouts.find(
          layout => layout.layoutName === search[0]?.value
        );

        if (!layoutMatch) throw new Error('No layout was found!');
        return layoutMatch;
      })
    );
  }

  getNextLayout(): Observable<SubjectAreaLayout> {
    const layoutId = this.activeLayout.layoutId;
    return this.layouts$.pipe(
      first(),
      map(layouts => {
        const layoutIndex = layouts.findIndex(
          layout => layout.layoutId === layoutId
        );

        const nextLayout = (layoutIndex + 1) % layouts.length;
        if (!layouts[nextLayout]) throw new Error('No layout was found!');
        return layouts[nextLayout];
      })
    );
  }

  public changeLayoutSelectedId(layoutId: number) {
    this.layoutSelectedId.next(layoutId);
  }
  public refreshPowerBiCharts(): void {
    this.refreshPowerBi.next();
  }

  getLayoutById(id: number, skip404: boolean): Observable<Layout> {
    const url = `${this.globalSettings.orApiUrl}${API_URL.layout}/findOneWithoutKpiDashboards/${id}`;
    if (!skip404) {
      return this.http.get<Layout>(url, { withCredentials: true });
    }
    return this.http.get<Layout>(url, {
      withCredentials: true,
      headers: {
        [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
          skipStatusCodes: [404],
        }).parseToString(),
      },
    });
  }

  isValidLayout(id: number, skip404: boolean): Observable<IsValidLayout> {
    const url = `${this.globalSettings.orApiUrl}${API_URL.layout}/${id}/isValid`;
    if (!skip404) {
      return this.http.get<IsValidLayout>(url, { withCredentials: true });
    }
    return this.http.get<IsValidLayout>(url, {
      withCredentials: true,
      headers: {
        [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
          skipStatusCodes: [404],
        }).parseToString(),
      },
    });
  }

  getAllLayouts(): Observable<DashboardMetadata[]> {
    const url = `${this.globalSettings.orApiUrl}${API_URL.layout}/dashboards`;
    return this.http.get<DashboardMetadata[]>(url, { withCredentials: true });
  }
}
